import { DatasetsService } from '../datasets/datasets.service';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild
} from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  AnonMapService,
  MapServiceData,
  MapServiceIdsList,
  MapServiceMetaData,
  MapServiceURLSlug,
  SelectList,
  ThemeSelectList,
} from '../../shared/models/map-services.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LicenceModalComponent } from './components/licence-modal/licence-modal.component';
import { PrivacyModalComponent } from './components/privacy-modal/privacy-modal.component';
import { ModalService } from '../../shared/services/modal.service';
import { UserService } from '../../shared/services/user.service';
import {
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations';

@Component({
  selector: 'app-access-data',
  templateUrl: './access-data.component.html',
  styleUrls: ['./access-data.component.scss'],
  animations: [
    trigger('toggleHvlspDownloadSection', [
      state('void', style({
        height: '0px',
        opacity: 0,
      })),
      state('*', style({
        height: '*',
        opacity: 1,
      })),
      // Custom cubic-bezier timing function
      transition('void <=> *', animate('500ms cubic-bezier(0.68, -0.55, 0.27, 1.55)'))
    ])
  ],
})
export class AccessDataComponent implements OnInit {
  onDestroy$: Subject<any> = new Subject<any>();
  datasets: AnonMapService[];
  submitIsSuccess: boolean = false;
  submitIsFailure: boolean = false;
  showHvlspDownload: boolean = false;
  submitting: boolean = false;
  form: FormGroup;
  selectAreaRadioValue: string = '1';
  themeList: string[] = [];
  countryList: string[] = [];
  administrativeAreaList: string[] = [];
  initialTextForCountrySelect: string = '';
  initialTextForAdministrativeAreaSelect: string = '';
  countrySelectInputFocusChecked: boolean = false;
  administrativeAreaSelectInputFocusChecked: boolean = false;
  themeSelectList: ThemeSelectList[] = [];
  countrySelectList: SelectList[] = [];
  administrativeAreaSelectList: SelectList[] = [];
  mapServiceData: MapServiceData = {
    mapServiceDownloadList: [],
    mapServiceURLSlugList: [],
  };
  mapServiceIdsList: MapServiceIdsList;
  mapsServiceDownloadDataLoading: boolean = false;
  mapsServiceDownloadError: boolean = false;
  mapsServiceDownloadErrorText: string = '';
  isInputFocused: boolean = false;

  @ViewChild('targetElementToScroll') targetElementToScroll!: ElementRef;

  constructor(
    private datasetsService: DatasetsService,
    private fb: FormBuilder,
    private userService: UserService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.getDatasets();
  }

  getDatasets(): void {
    this.datasetsService
      .getDatasets()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((datasets) => {
        this.datasets = datasets;
        this.form = this.fb.group({
          emailAddress: ['', Validators.email],
          marketingConsent: [false],
          licenseTerms: [false, Validators.requiredTrue],
          requestedServices: this.fb.array(
            this.datasets.map(({ id }) => {
              return this.fb.group({
                mapServiceId: [`${id}`],
                serviceType: this.fb.group({
                  DOWNLOAD: [false],
                  MAPSERVICE: [false],
                }),
              });
            }),
          ),
        });
      });
  }

  isHvlspDownloadChecked(mapServiceId: number, mapServiceURLSlug: string) {
    if (mapServiceURLSlug != 'hvlsp') {
      return;
    }
    this.showHvlspDownload = this.form.value.requestedServices.filter((item: any) => {
      if (item.mapServiceId == mapServiceId) {
        return item;
      }
    })[0]?.serviceType?.DOWNLOAD;

    // Reset all values
    const mapServiceMetadata = this.datasets.filter((item ) => {
      if (item.id === mapServiceId) {
        return item;
      }
    })[0]?.mapServiceMetadata;
    this.themeList = mapServiceMetadata['download-themes'];
    this.countryList = mapServiceMetadata['download-countries'];
    this.administrativeAreaList = mapServiceMetadata['download-adminunits'];
    this.selectAreaRadioValue = '1';
    this.themeSelectList = [];
    this.countrySelectList = [];
    this.administrativeAreaSelectList = [];
    this.countrySelectInputFocusChecked = false;
    this.administrativeAreaSelectInputFocusChecked = false;
    this.mapServiceData = {
      mapServiceDownloadList: [],
      mapServiceURLSlugList: [],
    };
  }

  public dropdownItemsForCountry = (text: string): Observable<any[]> => {
    return of(this.countryList);
  };

  public dropdownItemsForAdministrativeArea = (text: string): Observable<any[]> => {
    return of(this.administrativeAreaList);
  };

  scrollToElement(): void {
    if (this.targetElementToScroll) {
      const targetPosition = this.targetElementToScroll.nativeElement.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: targetPosition - 60, behavior: 'smooth' });
    }
  }

  onCountrySelectInputFocus(type: string) {
    this.isInputFocused = true;
    this.administrativeAreaSelectList = [];
    this.scrollToElement();

    if (type === 'add' || type === 'remove') {
      this.onSelectChange('radio', '2');
    }
    if (type === 'focus' && this.selectAreaRadioValue !== '2') {
      this.onSelectChange('radio', '2');
    }

    if (!this.countrySelectInputFocusChecked) {
      this.initialTextForCountrySelect = 'loading...';
      setTimeout(() => {
        this.initialTextForCountrySelect = '';
        this.countrySelectInputFocusChecked = true;
      },650);
    }
  }

  onAdministrativeAreaSelectInputFocus(type: string) {
    this.countrySelectList = [];
    this.scrollToElement();

    if (type === 'add' || type === 'remove') {
      this.onSelectChange('radio', '3');
    }
    if (type === 'focus' && this.selectAreaRadioValue !== '3') {
      this.onSelectChange('radio', '3');
    }

    if (!this.administrativeAreaSelectInputFocusChecked) {
      this.initialTextForAdministrativeAreaSelect = 'loading...';
      setTimeout(() => {
        this.initialTextForAdministrativeAreaSelect = '';
        this.administrativeAreaSelectInputFocusChecked = true;
      }, 650);
    }
  }

   onInputBlur(): void {
    this.isInputFocused = false;
  }

  updateCountryOnAutoFill(inputText: any): void {
    if (inputText !== '' && !inputText.includes('loading') && !this.isInputFocused) {
      // Reset input text
      this.initialTextForCountrySelect = 'loading...';
      setTimeout(() => {
        this.initialTextForCountrySelect = '';
        this.cdr.detectChanges();
        this.countrySelectInputFocusChecked = true;
      },650);
    }
  }

  updateAdministrativeAreaOnAutoFill(inputText: any): void {
    if (inputText !== '' && !inputText.includes('loading') && !this.isInputFocused) {
      // Reset input text
      this.initialTextForAdministrativeAreaSelect = 'loading...';
      setTimeout(() => {
        this.initialTextForAdministrativeAreaSelect = '';
        this.cdr.detectChanges();
        this.administrativeAreaSelectInputFocusChecked = true;
      },650);
    }
  }

  onSelectChange(type: string, event: any, item?: string) {
    if (type === 'checkbox') {
      let themeSelect: ThemeSelectList = { name: '', urlSlug: '' };
        themeSelect.name = item;
      if (item === 'Administrative Units') {
        themeSelect.urlSlug = 'Administrative Units';
      }
      if (item === 'Transportation') {
        themeSelect.urlSlug = 'Transport';
      }
      if (item === 'Hydrography') {
        themeSelect.urlSlug = 'Hydrography';
      }
      if (event.target.checked) {
        this.themeSelectList.push(themeSelect);
      } else {
        const index = this.themeSelectList.findIndex(theme => theme.name === themeSelect.name);
        if (index > -1) {
          this.themeSelectList.splice(index, 1);
        }
      }
    }

    if (type === 'radio') {
      if (typeof event === 'string') {
        this.selectAreaRadioValue = event;
      } else {
        // Empty all old selections
        this.countrySelectList = [];
        this.administrativeAreaSelectList = [];

        this.selectAreaRadioValue = event.target.value;
      }
    }

    if (this.hvlspDownloadIsValid()) {
      this.mapServiceData = {
        mapServiceDownloadList: [],
        mapServiceURLSlugList: [],
      };
      if (this.selectAreaRadioValue === '2') {
        this.themeSelectList.forEach((themeItem: ThemeSelectList) => {
          if (this.countrySelectList.length > 0) {
            this.countrySelectList.forEach((countryItem: SelectList) => {
              const mapServiceURLSlug = `high-value-largescale-prototype-${themeItem.urlSlug.trim().toLowerCase().replace(/\s+/g, '-')}-${countryItem.value.trim().toLowerCase().replace(/\s+/g, '-')}`;
              this.mapServiceData.mapServiceURLSlugList.push(mapServiceURLSlug);
              this.mapServiceData.mapServiceDownloadList.push({
                name: `${themeItem.name.trim()} (${countryItem.value.trim()})`,
                urlSlug: mapServiceURLSlug,
              });
            });
          }
        });
      } else if (this.selectAreaRadioValue === '3') {
        this.themeSelectList.forEach((themeItem: ThemeSelectList) => {
          if (this.administrativeAreaSelectList.length > 0) {
            this.administrativeAreaSelectList.forEach((administrativeAreaItem: SelectList) => {
              const mapServiceURLSlug = `high-value-largescale-prototype-${themeItem.urlSlug.trim().toLowerCase().replace(/\s+/g, '-')}-${administrativeAreaItem.value.trim().toLowerCase().split(', ')[0].replace(/\s+/g, '-')}`;
              this.mapServiceData.mapServiceURLSlugList.push(mapServiceURLSlug);
              this.mapServiceData.mapServiceDownloadList.push({
                name:`${themeItem.name.trim()} (${administrativeAreaItem.value.trim()})`,
                urlSlug: mapServiceURLSlug,
              });
            });
          }
        });
      } else {
        this.themeSelectList.forEach((themeItem: ThemeSelectList) => {
          const mapServiceURLSlug = `high-value-largescale-prototype-${themeItem.urlSlug.trim().toLowerCase().replace(/\s+/g, '-')}`;
          this.mapServiceData.mapServiceURLSlugList.push(mapServiceURLSlug);
          this.mapServiceData.mapServiceDownloadList.push({
            name: `${themeItem.name.trim()}`,
            urlSlug: mapServiceURLSlug,
          });
        });
      }

      if (this.mapServiceData.mapServiceURLSlugList.length > 0) {
        const mapServiceURLSlugListString: MapServiceURLSlug = { mapservice_slugs: this.mapServiceData.mapServiceURLSlugList };
        this.getMapServiceIds(mapServiceURLSlugListString);
      }
    } else {
      this.mapServiceData = {
        mapServiceDownloadList: [],
        mapServiceURLSlugList: [],
      };
    }
  }

  hvlspDownloadIsValid(): boolean {
    if (this.themeSelectList.length > 0) {
      if ((this.selectAreaRadioValue === '2' && this.countrySelectList.length > 0) || (this.selectAreaRadioValue === '3' && this.administrativeAreaSelectList.length > 0)) {
        return true;
      } else return this.selectAreaRadioValue === '1';
    }
    return false;
  }

  getMapServiceIds(mapServiceURLSlugList: MapServiceURLSlug) {
    this.mapsServiceDownloadDataLoading = true;
      this.datasetsService
      .getMapServiceIds(mapServiceURLSlugList)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data: any) => {
          this.mapServiceIdsList = data;
          this.mapServiceData.mapServiceDownloadList = this.mapServiceData.mapServiceDownloadList.map(item => {
            const foundValue = this.mapServiceIdsList.mapservices.find((findItem: MapServiceMetaData) => findItem.mapservice_url_slug === item.urlSlug);
            if (foundValue) {
              return { ...item, id: foundValue.id, sizeInBytes: foundValue.download_metadata?.downloads[0].sizeInBytes || 0 };
            }
            return item;
          });
          this.mapsServiceDownloadDataLoading = false;
          this.mapsServiceDownloadError = false;
          this.mapsServiceDownloadErrorText = '';
        },
        error: (err) => {
          this.mapsServiceDownloadDataLoading = false;
          this.mapsServiceDownloadError = true;
          this.mapsServiceDownloadErrorText = err.error.errorMessage;
        }
      });
  }

  emailIsInvalid(): boolean {
    const emailControl = this.form.get('emailAddress');
    return emailControl.invalid && emailControl.touched;
  }

  resetForm(): void {
    this.submitIsFailure = false;
    this.submitIsSuccess = false;
    this.getDatasets();
  }

  licenseTermsIsInvalid(): boolean {
    const licenseTermsControl = this.form.get('licenseTerms');
    return licenseTermsControl.invalid && licenseTermsControl.touched;
  }

  formIsValid(): boolean {
    return (
      this.form
        .get('requestedServices')
        .value.some(({ serviceType: { DOWNLOAD, MAPSERVICE } }) => DOWNLOAD || MAPSERVICE) &&
      this.form.valid
    );
  }

  getTooltipText(): string {
    if (!this.formIsValid() && this.showHvlspDownload) {
      if (!this.hvlspDownloadIsValid()) {
        return 'Please complete all the above download selections.';
      }
      if (this.form.get('licenseTerms').invalid) {
        return 'You must accept the licence terms to proceed.';
      }
    }
    return '';
  }

  submit() {
    this.form.markAsTouched();
    if (this.formIsValid()) {
      this.mapServiceData.mapServiceDownloadList.map((item: any) => {
        this.form.value.requestedServices.push({
          mapServiceId: item.id.toString(),
          serviceType: {
            DOWNLOAD: true,
            MAPSERVICE: false,
          }
        });
      });

      this.submitting = true;
      this.form.value.requestedServices.forEach((item: any) => {
        // Setting the download to false for hvlsp mapservice
        if (item.mapServiceId === '490') {
          item.serviceType.DOWNLOAD = false;
        }
        return item;
      });
      const { requestedServices, licenseTerms, ...otherFields } = this.form.value;
      const requestBody = {
        ...otherFields,
        requestedServices: requestedServices.reduce((acc, { serviceType, mapServiceId }) => {
          return [
            ...acc,
            ...Object.entries(serviceType)
              .filter(([_, value]) => value)
              .map(([key]) => ({ mapServiceId, serviceType: key })),
          ];
        }, []),
      };
      this.form.reset();
      this.userService
        .createRegisteredUser(requestBody)
        .pipe(finalize(() => (this.submitting = false)))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: () => {
            this.submitIsSuccess = true;
          },
          error: () => {
            this.submitIsFailure = true;
          },
        });
      this.showHvlspDownload = false;
    }
  }

  get loading(): boolean {
    return this.datasetsService.datasetsLoading;
  }

  openLicenseTermsModal(): void {
    this.modalService.openModal(LicenceModalComponent);
  }

  openPrivacyModal(): void {
    this.modalService.openModal(PrivacyModalComponent);
  }

  /**
   * @returns void
   */
  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }
}
